import { EventType } from "@azure/msal-browser"
import { toast } from "sonner"
import { msalInstance } from "@/auth/msalConfig"
import { staffAccountKey } from "@/auth/staffAccount"
import { getAccessTokenSilentOrNull } from "@/auth/getAccessToken"
import { decodeStaffAccessContext, StaffAccessDeniedError } from "@/services/staffAccessContext"
import { createStaffSessionRuntime } from "@/services/staffSessionRuntime"
import { registerStaffAccessRecheck } from "@/services/staffTransportSession"
import { onNotification, onReconnect, onConnectionStatus, startConnection, stopConnection, recoverConnection } from "@/lib/signalr"
import { handleNotification } from "@/hooks/notificationHandler"
import { store } from "./index"
import { staffSessionActions } from "./staffSessionSlice"
import { setUser, clearUser } from "./userSlice"
import { acceptStaffAccessContext } from "./featuresSlice"

declare const __API_BASE_URL__: string | undefined
const API_BASE_URL = typeof __API_BASE_URL__ !== "undefined" ? __API_BASE_URL__ : ""
let installed = false, suspended = false
let poll: ReturnType<typeof setTimeout> | undefined
const identity = () => suspended ? null : staffAccountKey()

function schedulePoll() {
  if (poll) return
  poll = setTimeout(() => {
    poll = undefined
    if (document.visibilityState === "visible") void staffSessionRuntime.refresh()
    else schedulePoll()
  }, 45_000 + Math.floor(Math.random() * 15_000))
}

export const staffSessionRuntime = createStaffSessionRuntime({
  identity,
  read: async signal => {
    const owner = identity()
    // A background refresh never launches interactive login or retries a write.
    const token = await getAccessTokenSilentOrNull()
    if (!owner || !token || owner !== identity()) throw new Error("Authentication unavailable.")
    const response = await fetch(`${API_BASE_URL}/api/users/me/access-context`, {
      signal, cache: "no-store", headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
    })
    if (response.status === 401 || response.status === 403) throw new StaffAccessDeniedError()
    if (!response.ok) throw new Error("Staff access unavailable.")
    return decodeStaffAccessContext(await response.json())
  },
  revoke: () => {
    if (poll) clearTimeout(poll)
    poll = undefined
    store.dispatch(staffSessionActions.scopeRevoked())
    store.dispatch(clearUser())
  },
  checking: () => { store.dispatch(staffSessionActions.checkingStarted()) },
  accept: context => {
    // Also reads the M12 flags once per new scope (featuresSlice.acceptStaffAccessContext).
    store.dispatch(acceptStaffAccessContext(context))
    const account = msalInstance.getActiveAccount()
    if (account) store.dispatch(setUser({ name: account.name ?? account.username, email: account.username }))
    schedulePoll()
  },
  unavailable: () => { store.dispatch(staffSessionActions.contextUnavailable()); schedulePoll() },
  startRealtime: key => window.self === window.top ? startConnection(key) : Promise.resolve(),
  stopRealtime: stopConnection,
})

/** Installed once for the authenticated tab, not stopped by StrictMode effect cleanup. */
export function ensureStaffSession() {
  if (!installed) {
    installed = true
    registerStaffAccessRecheck(() => { void staffSessionRuntime.accessHint() })
    onConnectionStatus(status => { store.dispatch(staffSessionActions.connectionChanged(status)) })
    onNotification((type, payload) => handleNotification(type, payload, {
      dispatch: store.dispatch, toast, accessChanged: () => { void staffSessionRuntime.accessHint() },
    }))
    onReconnect(() => { void staffSessionRuntime.refresh() })
    const foreground = () => {
      if (document.visibilityState !== "visible") return
      void staffSessionRuntime.refresh()
      recoverConnection()
    }
    window.addEventListener("focus", foreground)
    window.addEventListener("online", foreground)
    document.addEventListener("visibilitychange", foreground)
    window.addEventListener("storage", () => { void staffSessionRuntime.ensure() })
    msalInstance.addEventCallback(event => {
      if (event.eventType === EventType.LOGOUT_START) {
        suspended = true
        void staffSessionRuntime.dispose().catch(() => {})
      } else if (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.LOGOUT_END) {
        suspended = false
        void staffSessionRuntime.ensure()
      } else if (event.eventType === EventType.ACTIVE_ACCOUNT_CHANGED) {
        void staffSessionRuntime.ensure()
      }
    })
  }
  return staffSessionRuntime.ensure()
}
